import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useSelector, useStore } from 'react-redux';

import { logArticleVideoPlay } from '../../../bi';
import { reportItemOnViewWhenExpanded } from '../../../bi/reportItemOnViewWhenExpanded';
import { createArticleSolutionInfo } from '../../../bi/solutionInfo';
import { getSharedBiFieldsFromState } from '../../../bi/utils/sharedBiFields';
import { selectArticleState } from '../../../common/selectors/helpCenter';
import { getYouTubeVideoId, whenYouTubeApiReady } from '../../../util/youtube';

declare global {
  interface Window {
    YT: any;
  }
}

export function useYouTubeVideoTracker(containerRef: RefObject<HTMLDivElement>, articleId: string): void {
  const store = useStore();
  const { isLoading } = useSelector(selectArticleState);
  const playersRef = useRef<any[]>([]);
  const viewedVideosRef = useRef<Set<string>>(new Set());

  const handleVideoPlay = useCallback(
    (videoUrl: string) => {
      const state = store.getState();
      logArticleVideoPlay(state, { videoUrl, articleId });
    },
    [store, articleId],
  );

  const handleVideoView = useCallback(
    async (videoId: string) => {
      if (viewedVideosRef.current.has(videoId)) {
        return;
      }

      const state = store.getState();
      const sharedBiFields = await getSharedBiFieldsFromState(state);
      const presentedContent = createArticleSolutionInfo({ articleId });

      const sent = await reportItemOnViewWhenExpanded(state, {
        widget_language: sharedBiFields.widget_language,
        self_service_session_id: sharedBiFields.self_service_session_id,
        widget_page_type: 'article',
        presented_content: JSON.stringify(presentedContent),
        item_name: `article_video`,
      });

      if (sent) {
        viewedVideosRef.current.add(videoId);
      }
    },
    [articleId, store],
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const containerElement = containerRef.current;
    const viewedVideos = viewedVideosRef.current;

    if (!containerElement) {
      return;
    }

    const videoIframes = containerElement.querySelectorAll('.component.video-component iframe');

    if (videoIframes.length === 0) {
      return;
    }

    const videoData: { id: string; src: string; videoId: string }[] = [];

    videoIframes.forEach((iframe, index) => {
      const src = iframe.getAttribute('src');
      if (src) {
        const videoId = getYouTubeVideoId(src);
        if (videoId) {
          const uniqueId = `yt-video-${videoId}-${index}`;
          iframe.setAttribute('id', uniqueId);
          videoData.push({ id: uniqueId, src, videoId });
        }
      }
    });

    if (videoData.length === 0) {
      return;
    }

    // Set up visibility tracking
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(async (entry) => {
          const { target, isIntersecting } = entry;
          const id = target.getAttribute('id');
          const videoDataItem = videoData.find((item) => item.id === id);

          if (id && videoDataItem && isIntersecting) {
            await handleVideoView(videoDataItem.videoId);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      },
    );

    videoIframes.forEach((iframe) => {
      observer.observe(iframe);
    });

    const setupVideoTracking = () => {
      videoData.forEach(({ id, src }) => {
        if (!window.YT || !document.getElementById(id)) {
          return;
        }
        try {
          const player = new window.YT.Player(id, {
            events: {
              onStateChange: (event: any) => {
                if (event.data === 1) {
                  handleVideoPlay(src);
                }
              },
            },
          });
          playersRef.current.push(player);
        } catch {
          // The iframe was replaced between the API becoming ready and this binding.
        }
      });
    };

    const unsubscribeFromYouTubeApi = whenYouTubeApiReady(setupVideoTracking);

    return () => {
      observer.disconnect();
      unsubscribeFromYouTubeApi();

      playersRef.current.forEach((player) => {
        if (player && typeof player.destroy === 'function') {
          player.destroy();
        }
      });
      playersRef.current = [];
      viewedVideos.clear();
    };
  }, [containerRef, articleId, handleVideoPlay, handleVideoView, isLoading]);
}
